// Vertex traversal: given an entering edge and a directional preference,
// resolve which of the two exit edges the player takes.
//
// From game-design-doc §4 and §6: U-turns are illegal; the entry edge is
// never chosen as an exit. The two remaining exits are "left-ish" and
// "right-ish" from the player's point of view, always well-defined.

import type { HexGrid, EdgeId, VertexId } from './grid.js';

export type DirectionPref = 'left' | 'right';

export interface Exits {
  left: EdgeId;
  right: EdgeId;
}

/**
 * Given a vertex and the edge the player just arrived on, return the two
 * non-entry exits classified as left and right.
 *
 * The three vertex edges are treated as a fixed cyclic sequence [e0, e1, e2].
 * The exit pair is a cyclic rotation that excludes the entry:
 *   entry=e0 → left=e1, right=e2
 *   entry=e1 → left=e2, right=e0
 *   entry=e2 → left=e0, right=e1
 *
 * Vertex IDs are contiguous from 0, so grid.vertices[vertex] is O(1).
 */
export function exitsFrom(
  grid: HexGrid,
  vertex: VertexId,
  entryEdge: EdgeId,
): Exits {
  const v = grid.vertices[vertex];
  if (v === undefined || v.id !== vertex) {
    throw new Error(`Unknown vertex ${vertex}`);
  }
  const [e0, e1, e2] = v.edges;
  if (entryEdge === e0) return { left: e1, right: e2 };
  if (entryEdge === e1) return { left: e2, right: e0 };
  if (entryEdge === e2) return { left: e0, right: e1 };
  throw new Error(`Edge ${entryEdge} is not incident to vertex ${vertex}`);
}

export function resolveExit(exits: Exits, pref: DirectionPref): EdgeId {
  return pref === 'left' ? exits.left : exits.right;
}
